import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col
} from 'reactstrap';
import { Link } from 'react-router-dom';
import {
  fetchReportTemplate,
  updateReportTemplate,
  uploadReportAttachment,
  removeReportAttachment,
  downloadReportAttachment
} from '../../actions/companies/apiRequests';
import { REPORT_DISPLAY_NAMES } from '../../constants/reportLayouts';
import {
  notifyInfo,
  notifyError
} from '../../utils/notifier';
import { ADMINS } from '../../constants/userRoles';
import Page from '../shared/Page';
import SaveButton from '../shared/SaveButton';
import CancelButton from '../shared/CancelButton';
import withRoleRestrictions from '../shared/withRoleRestrictions';
import Attachment from './Attachment';
import CoverLetter from './CoverLetter';
import EndOfReport from './EndOfReport';
import Modal from '../shared/Modal';
import {
  REPORT_MANAGEMENT,
  DASHBOARD,
  PAGE_PATHS
} from '../../constants/pathsRoots';
import { getBreadCrumbs } from '../../utils/breadCrumbs';


export class ReportLayoutPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasAttachment: false,
      attachment: null,
      attachmentDeleted: false,
      coverLetter: null,
      endOfReport: null,
      attachmentCheck: false,
      newAttachment: null,
      attachmentFilename: null,
      fileUploadError: false
    };

    this.uploadAttachment = this.uploadAttachment.bind(this);
    this.removeAttachment = this.removeAttachment.bind(this);
    this.changeEndOfReport = this.changeEndOfReport.bind(this);
    this.changeCoverLetter = this.changeCoverLetter.bind(this);
    this.saveChanges = this.saveChanges.bind(this);
    this.downloadAttachment = this.downloadAttachment.bind(this);
    this.onModalConfirm = this.onModalConfirm.bind(this);
    this.onModalClose = this.onModalClose.bind(this);
    this.showModalError = this.showModalError.bind(this);
    this.hideAttachmentUploadError = this.hideAttachmentUploadError.bind(this);
  }

  static get propTypes() {
    return {
      layout: PropTypes.string,
      reportType: PropTypes.string,
      companyId: PropTypes.string,
      withCoverLetter: PropTypes.bool,
      withEndOfReport: PropTypes.bool,
      withAttachment: PropTypes.bool,
      coverLetterTitle: PropTypes.string
    }
  }

  get actionButtons() {
    const { companyId } = this.props;
    return [
      <Link
        to={{
        pathname: '/reports',
        state: { companyId }
      }}
        key='cancel'
      >
        <CancelButton />
      </Link>,
      <Link
        to={{
          pathname: '/reports',
          state: { companyId }
        }}
        key='save'
      >
        <SaveButton
          key='save'
          onClick={this.saveChanges}
        />
      </Link>,
    ];
  }

  get pageTitle() {
    const {
      layout,
      reportType
    } = this.props;
    return `Edit ${REPORT_DISPLAY_NAMES[layout]} - ${reportType} Template`;
  }

  componentDidMount() {
    const {
      companyId,
      reportType,
      layout
    } = this.props;

    window.scrollTo(0, 0);

    fetchReportTemplate(companyId, layout, reportType)
      .then((response) => {
        this.setState({
          coverLetter: response.coverLetter,
          endOfReport: response.endOfReport,
          hasAttachment: response.hasAttachment,
          attachmentFilename: response.attachmentFilename
        });
      })
  }

  render() {
    const {
      hasAttachment,
      attachment,
      attachmentDeleted,
      coverLetter,
      endOfReport,
      attachmentCheck,
      attachmentFilename,
      fileUploadError
    } = this.state;
    const {
      withCoverLetter,
      withEndOfReport,
      withAttachment,
      coverLetterTitle
    } = this.props;
    return (
      <Page
        header={this.pageTitle}
        actions={this.actionButtons}
        breadCrumbs={getBreadCrumbs('Edit Report', '', [PAGE_PATHS[DASHBOARD], PAGE_PATHS[REPORT_MANAGEMENT]])}
      >
        <Row className='mb-4'>
          <Col>
            <p>The settings on this page will be included each time the report template is generated</p>
          </Col>
        </Row>
        <If condition={withCoverLetter}>
          <Row className='mb-4'>
            <Col xl={8}>
              <CoverLetter
                value={coverLetter}
                title={coverLetterTitle}
                onChange={this.changeCoverLetter}
              />
            </Col>
          </Row>
        </If>
        <If condition={withEndOfReport}>
          <Row className='mb-4'>
            <Col xl={8}>
              <EndOfReport
                value={endOfReport}
                onChange={this.changeEndOfReport}
              />
            </Col>
          </Row>
        </If>
        <If condition={withAttachment}>
          <Row className='mb-5'>
            <Col xl={8} className='mb-5'>
              <Attachment
                hasAttachment={hasAttachment}
                file={attachment}
                attachmentDeleted={attachmentDeleted}
                onUpload={this.uploadAttachment}
                onRemove={this.removeAttachment}
                downloadAttachment={this.downloadAttachment}
                attachmentFilename={attachmentFilename}
                modalError={this.showModalError}
              />
            </Col>
          </Row>
        </If>

        <Modal
          isOpen={fileUploadError}
          showCancelButton={false}
          onConfirm={this.hideAttachmentUploadError}
          header='File upload error'
        >
          File cannot be uploaded. Make sure that file extension is PDF and its size less than 4Mb
        </Modal>

        <Modal
          isOpen={attachmentCheck}
          showCancelButton={true}
          onCancel={this.onModalClose}
          onConfirm={this.onModalConfirm}
          header='File upload error'
        >
          Only one attachment can be uploaded per report. Would you like to overwrite the PDF that is currently attached to the report?
        </Modal>
      </Page>
    );
  };

  showModalError() {
    this.setState({
      fileUploadError: true
    });
  }

  uploadAttachment(files, rejectedFiles = []) {
    const rejectedFile = rejectedFiles[0];

    if (rejectedFile) {
      this.setState({
        fileUploadError: true
      });
      return;
    }

    if (files && files[0]) {
      if (this.state.attachment) {
        this.setState({
          newAttachment: files[0],
          attachmentCheck: true
        });
        return;
      }
      this.setState({
        attachment: files[0]
      });
    }
  }

  onModalClose() {
    this.setState({
      attachmentCheck: false
    });
  }

  onModalConfirm() {
    this.setState((prevState) => ({
        attachment: prevState.newAttachment,
        newAttachment: null,
        attachmentCheck: false
      })
    );
  }

  hideAttachmentUploadError() {
    this.setState({
      fileUploadError: false
    });
  }

  removeAttachment() {
    this.setState(({ hasAttachment }) => ({
      attachment: null,
      attachmentDeleted: hasAttachment
    }));
  }

  changeEndOfReport(event) {
    this.setState({
      endOfReport: event.target.value
    });
  }

  changeCoverLetter(event) {
    this.setState({
      coverLetter: event.target.value
    });
  }

  downloadAttachment() {
    const {
      companyId,
      layout,
      reportType
    } = this.props;

    return downloadReportAttachment(companyId, layout, reportType);
  }

  updateAttachment() {
    const {
      companyId,
      layout,
      reportType
    } = this.props;
    const {
      attachment,
      attachmentDeleted
    } = this.state;

    if (attachment) {
      return uploadReportAttachment(companyId, attachment, layout, reportType);
    }

    if (attachmentDeleted) {
      return removeReportAttachment(companyId, layout, reportType);
    }

    return Promise.resolve();
  }

  saveChanges() {
    const {
      coverLetter,
      endOfReport,
    } = this.state;
    const {
      companyId,
      layout,
      reportType
    } = this.props;

    return updateReportTemplate(
      companyId,
      layout,
      reportType,
      coverLetter,
      endOfReport
    )
      .then(() => this.updateAttachment())
      .then(() => notifyInfo())
      .catch(() => notifyError())
  }
}

export default withRoleRestrictions(ReportLayoutPage, ADMINS)


// WEBPACK FOOTER //
// src/components/reportLayout/ReportLayoutPage.jsx